import type { SessionContext } from '../context/session-context.js'
import { createUserContext, type UserContext } from '../context/user-context.js'
import { CacheWritingService } from './persistence/cache-writing-service.js'
import type { FamilyLocalBacking } from './persistence/family-local-backing.js'
import {
    decodeUserContextRecord,
    type UserContextRecord
} from './persistence/stores/user-context-store.js'

export class UserContextCache extends CacheWritingService {
    readonly #localBacking: FamilyLocalBacking

    public constructor(localBacking: FamilyLocalBacking) {
        super()
        this.#localBacking = localBacking
    }

    public clear(): Promise<void> {
        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getUserContextStore()
            await store?.clear()
        })
    }

    public put(
        sessionContext: SessionContext,
        context: UserContext,
        signal?: AbortSignal
    ): Promise<void> {
        return this.enqueueCacheWrite(async () => {
            if (signal?.aborted) {
                return
            }

            const store = await this.#localBacking.getUserContextStore()

            if (store === undefined || signal?.aborted) {
                return
            }

            const existingRecord = await store.get()

            if (signal?.aborted) {
                return
            }

            this.#assertCurrentUserInvariant(existingRecord, sessionContext, context.person.key)
            await store.put(
                decodeUserContextRecord({
                    cachedAt: Date.now(),
                    namespace: sessionContext.namespace,
                    person: context.person
                })
            )
        })
    }

    public async restore(
        sessionContext: SessionContext,
        signal: AbortSignal
    ): Promise<UserContext | null> {
        try {
            const store = await this.#localBacking.getUserContextStore()

            if (store === undefined || signal.aborted) {
                return null
            }

            const record = await store.get()

            if (
                record === null ||
                record.namespace !== sessionContext.namespace ||
                signal.aborted
            ) {
                return null
            }

            return createUserContext(record.person)
        } catch (error) {
            console.warn('Unable to restore user context from persistence.', error)
            return null
        }
    }

    #assertCurrentUserInvariant(
        record: UserContextRecord | null,
        sessionContext: SessionContext,
        personKey: string
    ): void {
        if (record === null) {
            return
        }

        if (record.namespace === sessionContext.namespace && record.person.key !== personKey) {
            throw new Error('Session namespace cannot resolve to a different person.')
        }

        if (record.namespace !== sessionContext.namespace && record.person.key === personKey) {
            throw new Error('Person identity cannot resolve from a different namespace.')
        }
    }
}
